<template>
  <div class="attendance-governance-addon">
    <div class="addon-head">
      <div>
        <span>HRM_ATTENDANCE_GOVERNANCE</span>
        <h2>智能考勤治理洞察</h2>
        <p>聚合考勤、请假、员工和部门数据，识别异常出勤、部门风险、员工风险排行与请假审批积压。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门考勤风险排行</div>
          <el-table :data="overview.deptRiskRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="attendance_count" label="考勤数" width="90" />
            <el-table-column prop="abnormal_count" label="异常数" width="90" />
            <el-table-column prop="absenteeism_count" label="旷工" width="80" />
            <el-table-column prop="abnormal_rate" label="异常率" width="90">
              <template slot-scope="{ row }">{{ row.abnormal_rate || 0 }}%</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">考勤状态分布</div>
          <div class="status-list">
            <div v-for="item in overview.statusDistribution" :key="item.status" class="status-item">
              <span>{{ formatStatus(item.status) }}</span>
              <strong>{{ item.status_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">员工异常排行</div>
          <el-table :data="overview.staffRiskRanking" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="120" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="late_count" label="迟到" width="80" />
            <el-table-column prop="early_leave_count" label="早退" width="80" />
            <el-table-column prop="absenteeism_count" label="旷工" width="80" />
            <el-table-column prop="risk_count" label="风险数" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">请假趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.leaveTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.leave_count }}</strong>
              <em>{{ item.leave_days || 0 }} 天 / 待审 {{ item.pending_count || 0 }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getAttendanceGovernanceOverview } from '../api/attendanceGovernance'

export default {
  name: 'AttendanceGovernanceOverview',
  data () {
    return {
      loading: false,
      overview: {
        attendanceTotal: 0,
        abnormalAttendance: 0,
        absenteeismCount: 0,
        leaveAttendanceCount: 0,
        pendingLeaveCount: 0,
        activeStaffCount: 0,
        deptRiskRanking: [],
        staffRiskRanking: [],
        statusDistribution: [],
        leaveTrend: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '考勤记录', value: this.overview.attendanceTotal, meta: `${this.overview.activeStaffCount || 0} 名在职员工` },
        { label: '异常考勤', value: this.overview.abnormalAttendance, meta: '迟到 / 早退 / 旷工', tone: 'red' },
        { label: '旷工记录', value: this.overview.absenteeismCount, meta: '需重点复核', tone: 'orange' },
        { label: '待审请假', value: this.overview.pendingLeaveCount, meta: `${this.overview.leaveAttendanceCount || 0} 条休假考勤`, tone: 'blue' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getAttendanceGovernanceOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '智能考勤治理洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatStatus (status) {
      const map = {
        0: '正常',
        1: '迟到',
        2: '早退',
        3: '旷工',
        4: '休假'
      }
      return map[Number(status)] || '未知'
    }
  }
}
</script>

<style lang="less" scoped>
.attendance-governance-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #2f6fec;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.status-item span,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 18px;
}
</style>
